import {Box} from '@mantine/core';
import React, {useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {
  type FlowchartConnectionOptions,
  FlowchartConnectionMode,
  getProcedureMethodEdgeKey,
} from 'studies/typed/shared/flowcharts/design/procedureRunDesignFlowchartConnectionUtils';
import ProcedureRunDesignFlowchartEditor from 'studies/typed/shared/flowcharts/design/ProcedureRunDesignFlowchartEditor';
import {
  methodLinksFromProcedureEdges,
  PlanConditionReplicateEdge,
} from 'studies/typed/shared/flowcharts/design/ProcedureRunDesignFlowchartLayoutUtils';
import {useProcedureRunDesignFlowchartEdgeSpecs} from 'studies/typed/shared/flowcharts/design/useProcedureRunDesignFlowchartEdgeSpecs';
import {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {UseStudyDesignStateResult} from 'studies/typed/shared/procedure-runs/design-state/useStudyDesignState';
import type {LeftPanelView} from 'studies/typed/shared/procedure-runs/design-tabs/ProcedureRunDesignEditorLeftSidepanel';

import PublishedProcedureVersionUpdateBanner from '../procedure-selection/PublishedProcedureVersionUpdateBanner';
import {FlowchartProcedureRunDesignOptOutBanner} from './FlowchartProcedureRunDesignPreviewBanners';
import PlanSettingsSection from './PlanSettingsSection';
import ProcedureRunDesignEditorRightSidepanel from './ProcedureRunDesignEditorRightSidepanel';
import ProcedureRunDesignLeftSidepanelContent from './ProcedureRunDesignLeftSidepanelContent';
import {DataEntryProps, ProcedureProps, VersionUpdateProps} from './ProcedureRunPlanDesignContentTypes';
import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import UnitOperationsSection from './UnitOperationsSection';

interface PreviewContext {
  isOptOutBannerDismissed: boolean;
  onDismissOptOutBanner: () => void;
  onOptOut: () => Promise<void>;
}

interface FlowchartProcedureRunPlanDesignContentProps {
  conditionReplicateFlowchart: Array<PlanConditionReplicateEdge>;
  dataEntry: DataEntryProps;
  designState: UseStudyDesignStateResult<ConditionGroup>;
  onRemoveProcedure: () => Promise<void>;
  preview: PreviewContext;
  procedure: ProcedureProps;
  versionUpdate: VersionUpdateProps;
}

export default function FlowchartProcedureRunPlanDesignContent({
  conditionReplicateFlowchart,
  dataEntry,
  designState,
  onRemoveProcedure,
  preview,
  procedure,
  versionUpdate,
}: FlowchartProcedureRunPlanDesignContentProps): JSX.Element {
  const {
    conditions,
    context,
    editState,
    experimentalInputs,
    methodExecutionLimitState,
    selectedMethodId,
    setConditions,
    setExperimentalInputs,
    setSelectedMethodId,
    templateFlowchart,
  } = designState;

  const isPlanningTimeSplitPoolEnabled = BenchlingConfig.STUDIES_ENABLE_PLANNING_TIME_SPLIT_POOL;

  // Planning split/pool uses the plan's condition graph; otherwise template method links fan out
  // across each condition/replicate row to show the execution order in the flowchart.
  const edgeSpecs = useProcedureRunDesignFlowchartEdgeSpecs({
    isFlowchartProcedureRunDesignEnabled: true,
    conditions,
    methods: templateFlowchart.nodes,
    procedureEdges: templateFlowchart.edges,
    isPlanningTimeSplitPoolEnabled,
    planConditionReplicateFlowchart: conditionReplicateFlowchart,
  });

  const procedureMethodEdgeKeys = useMemo<ReadonlySet<string>>(
    () =>
      new Set(
        methodLinksFromProcedureEdges(templateFlowchart.edges).map((link) =>
          getProcedureMethodEdgeKey(link.sourceMethodId, link.targetMethodId)
        )
      ),
    [templateFlowchart.edges]
  );
  const connectionOptions: FlowchartConnectionOptions = isPlanningTimeSplitPoolEnabled
    ? {
        connectionMode: FlowchartConnectionMode.PROCEDURE_RUN_PLAN,
        procedureMethodEdgeKeys,
      }
    : {connectionMode: FlowchartConnectionMode.DISABLED};

  const combinedView = (
    <ProcedureRunDesignLeftSidepanelContent
      conditions={conditions}
      setConditions={setConditions}
      experimentalInputs={experimentalInputs}
      setExperimentalInputs={setExperimentalInputs}
      editState={editState}
      procedureType={procedure.type}
      methodExecutionLimitState={methodExecutionLimitState}
      studyType={procedure.studyType}
      dataEntryMode={dataEntry.mode}
      setDataEntryMode={dataEntry.setMode}
      procedureId={procedure.id}
      flowchartVersionId={procedure.flowchartVersionId}
      onRemoveProcedure={onRemoveProcedure}
      hasUpdateAvailable={versionUpdate.hasNewerVersion}
      isProcedureVersionUpdateDisabled={versionUpdate.hasUnsavedChanges}
      onOpenUpdateModal={versionUpdate.onOpenUpdateModal}
      isPlan={true}
    />
  );
  const leftPanelViews: Array<LeftPanelView> = isPlanningTimeSplitPoolEnabled
    ? [
        {
          icon: 'create',
          tooltip: 'Run plan',
          name: 'Run plan',
          content: (
            <Box pl="md" pr="md">
              <UnitOperationsSection
                conditions={conditions}
                experimentalInputs={experimentalInputs}
                methodExecutionLimitState={methodExecutionLimitState}
                methods={templateFlowchart.nodes}
                procedureType={procedure.type}
              />
            </Box>
          ),
        },
        {
          icon: 'gear',
          tooltip: 'Settings',
          name: 'Settings',
          content: (
            <Box pl="md" pr="md">
              <PlanSettingsSection
                editState={editState}
                procedureId={procedure.id}
                flowchartVersionId={procedure.flowchartVersionId}
                procedureType={procedure.type}
                studyType={procedure.studyType}
                onRemoveProcedure={onRemoveProcedure}
                hasUpdateAvailable={versionUpdate.hasNewerVersion}
                isProcedureVersionUpdateDisabled={versionUpdate.hasUnsavedChanges}
                onOpenUpdateModal={versionUpdate.onOpenUpdateModal}
                dataEntryMode={dataEntry.mode}
                setDataEntryMode={dataEntry.setMode}
              />
            </Box>
          ),
        },
      ]
    : [
        {
          icon: 'create',
          tooltip: 'Run Plan',
          name: 'Run Plan',
          content: combinedView,
        },
      ];

  return (
    <ProcedureRunDesignFlowchartEditor
      conditions={conditions}
      methods={templateFlowchart.nodes}
      activeMethodId={null} // Active method is not relevant for run plans
      procedureType={procedure.type}
      connectionOptions={connectionOptions}
      experimentalInputs={experimentalInputs}
      selectedMethodId={selectedMethodId}
      onChangeSelectedMethod={setSelectedMethodId}
      isInEditState={editState === StudyDesignTabEditState.EDITING}
      edgeSpecs={edgeSpecs}
      allowEditingPersistedConditions={true}
      topBanner={
        !preview.isOptOutBannerDismissed || versionUpdate.hasNewerVersion ? (
          <>
            {!preview.isOptOutBannerDismissed && (
              <FlowchartProcedureRunDesignOptOutBanner
                isPlan={true}
                onDismiss={preview.onDismissOptOutBanner}
                onOptOut={preview.onOptOut}
                procedureType={procedure.type}
              />
            )}
            {versionUpdate.hasNewerVersion && (
              <PublishedProcedureVersionUpdateBanner
                procedureType={procedure.type}
                onOpenUpdateModal={versionUpdate.onOpenUpdateModal}
                hasUnsavedChanges={versionUpdate.hasUnsavedChanges}
              />
            )}
          </>
        ) : null
      }
      leftPanelViews={leftPanelViews}
      // Render the Design panel contents only once a method is selected: the sidepanel requires a
      // non-null selectedMethodId, and the editor keeps the right panel closed without a selection.
      rightPanelChildren={
        selectedMethodId != null && (
          <ProcedureRunDesignEditorRightSidepanel
            templateFlowchart={templateFlowchart}
            selectedMethodId={selectedMethodId}
            procedureType={procedure.type}
            context={context}
            isProcedureRunPlan={true}
          />
        )
      }
    />
  );
}
